// sensitive-path-globs.ts
//
// Phase 06g R1d + R3 (security audit 2026-10-12 C1 branch 2, H2): paths whose
// contents run code OUTSIDE the diff a reviewer reads — test-runner config
// (conftest.py, package.json#scripts, vitest/jest config, Makefile, .npmrc)
// and CI/git-hook definitions. One list, one check (result-verifier-
// sensitive-path-checks.ts, step 5c): a worker may only change them when the
// ticket's acceptance names the file, which the G2 reviewer sees in the plan.
// Pure data + matcher; no I/O.
import { matchesGlob } from "./result-verifier-scope-checks.js";

// Re-audit 06g N3: every entry matches at ANY depth (pytest reads the ini
// nearest the common ancestor of its args; `npm run build` runs the build
// config of a workspace package) and is compared case-insensitively
// (isSensitivePath) — APFS is case-insensitive, `tests/Conftest.py` IS
// conftest.py to pytest. Build/lint config is in because `npm run build*` /
// `lint*` execute it. The list is still an allowlist of known shapes, not a
// sandbox — C1 branch 2 stays open (guide "Giới hạn đã biết").
export const SENSITIVE_TEST_CONFIG_GLOBS: readonly string[] = [
  "**/conftest.py",
  "**/pytest.ini",
  "**/.pytest.ini",
  "**/setup.cfg",
  "**/setup.py",
  "**/pyproject.toml",
  "**/tox.ini",
  "**/package.json",
  "**/vitest.config.*",
  "**/vitest.workspace.*",
  "**/jest.config.*",
  "**/.mocharc*",
  "**/.npmrc",
  "**/Makefile",
  "**/GNUmakefile",
  "**/tsconfig*.json",
  "**/vite.config.*",
  "**/tsup.config.*",
  "**/next.config.*",
  "**/webpack.config.*",
  "**/rollup.config.*",
  "**/babel.config.*",
  "**/.babelrc*",
  "**/.eslintrc*",
  "**/eslint.config.*",
];

/** R3 (H2): CI pipelines and git hooks — proposal scope also refuses these (đợt 2). */
export const SENSITIVE_CI_HOOK_GLOBS: readonly string[] = [
  ".gitlab-ci.yml",
  ".gitlab/**",
  ".github/**",
  ".husky/**",
  ".pre-commit-config.yaml",
  "**/lefthook.yml",
  ".git/**",
];

export const SENSITIVE_PATH_GLOBS: readonly string[] = [
  ...SENSITIVE_TEST_CONFIG_GLOBS,
  ...SENSITIVE_CI_HOOK_GLOBS,
];

/**
 * True when the repo-relative `path` is one of the sensitive files. A glob
 * prefixed by `**` + `/` also matches at the repo root (matchesGlob needs at
 * least one directory segment for that prefix). Case-insensitive on both
 * sides (N3): the controller's filesystem is, so a differently-cased name is
 * the same file to the tool that loads it.
 */
export function isSensitivePath(path: string): boolean {
  const candidate = path.toLowerCase();
  return SENSITIVE_PATH_GLOBS.map((g) => g.toLowerCase()).some(
    (glob) =>
      matchesGlob(glob, candidate) ||
      (glob.startsWith("**/") && matchesGlob(glob.slice(3), candidate)),
  );
}
